<template>
  <div class="tableWithPerPageSelection">
    <div class="entete border-entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">
                {{ $t('listeFactureAchat.title') }}
              </h1>
              <breadcrumb :breadcrumb="breadcrumb"/>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns mgt-20px">
      <form @submit.prevent="recherche()" class="column is-3">
        <b-input :placeholder="$t('general.button.search')"
                 type="search"
                 icon="magnify"
                 v-model="criteresRecherche.auto">
        </b-input>
      </form>
      <div class="column is-narrow">
        <button class="button" @click='showFiltreAndLockMenu()'>
          <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
          <span class="is-size-7">{{$t('general.button.filtre')}}
            <label class="has-text-weight-bold"
                    :class="nombreCritere > 0 ? '' : 'is-invisible'">
              ({{ nombreCritere }})
            </label>
          </span>
        </button>
        <span @click="reinitialiserFiltre()"
          class="info-circle-right is-pulled-right is-cursor-pointer">
          <b-icon icon="close-circle" type="is-danger"></b-icon>
        </span>
      </div>
    </div>
    <v-server-table url="" :columns="columns" :options="options" ref="tableListeFactureAchat"
                    @loaded="finLoading" @loading="debutLoading"
                    v-if="isMounted"
                    class="overflow-x-auto">
      <template slot='afterTable'>
        <table-items-in-view @input="$refs.tableListeFactureAchat.setLimit($event)"/>
      </template>
      <div slot="code" slot-scope="props">
        <router-link :to="{ name: 'c.baux.facture_achat',
                      params: { id: props.row.id } }"
                     class="lien" :class="{'text-access-no-permission':
                     !checkPermissions(constantes.FACTURE_ACHAT.lecture)}">
          {{ props.row.code }}
        </router-link>
      </div>
      <div slot="dateDevisCommande" slot-scope="props">
        {{ props.row.dateDevisCommande | formatDate }}
      </div>
      <div slot="montantHt" class="has-text-right" slot-scope="props">
        {{ props.row.montantHt | formatDevise }}
      </div>
      <div slot="sousType" slot-scope="props">
        {{ $t(getSousType(props.row.sousType))}}
      </div>
      <div slot="montantRegle" class="has-text-right" slot-scope="props">
        {{ props.row.montantRegle | formatDevise }}
      </div>
      <div slot="montantTtc" class="has-text-right" slot-scope="props">
        {{ props.row.montantTtc | formatDevise }}
      </div>
      <div slot="etat" slot-scope="props">
        {{ $t(getEtat(props.row.etat)) }}
      </div>
      <template #edit="{ row }">
        <router-link
          :to="{
            name: 'm.baux.facture_achat-save-to-edit',
            params: { id: row.id, aRapprocher: false },
          }"
          class="is-cursor-pointer">
          <b-icon
            :class="{'text-access-no-permission': !checkPermissions(constantes.FACTURE_ACHAT.maj)}"
            icon="pencil-box-outline"
            type="is-success">
          </b-icon>
        </router-link>
      </template>
    </v-server-table>
    <transition name="custom-classes-transition"
                enter-active-class="animate__animated animate__slideInRight"
                leave-active-class="animate__animated animate__slideOutRight" :duration="10">
      <div class="componentBrief" v-show="showFiltre === true">
        <filtre-facture-achat ref="filtreListeFactureAchat"
                              :critereRecherche="criteresRecherche"
                              :intervenantEcheance="intervenantsNames()"
                              :codeBail="codeBail"
                              :dateEcheance="dateEcheance"
                              @toggleFiltre="showFiltreAndLockMenu"
                              @recherche="makeRechercheFilter"
        ></filtre-facture-achat>
      </div>
    </transition>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import breadcrumb from '@/components/general/breadcrumb';
import subscribeMutation from '@/services/store/subscribeMutation';
import { SET_DETECT_CHANGE_SOCIETE, SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import {
  NO_PER_PAGE_VALUE, RESULTS_PER_PAGE, SORT_ICON, TEXTS, NO_PAGINATION,
} from '@/configuration/clientTableConf';
import cloneDeep from 'lodash/cloneDeep';
import { SENS_ACHAT } from '@/configuration/devisConf';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import TableItemsInView from '@/components/general/TableItemsInView';
import { FACTURE_ACHAT } from '@/configuration/baux/permissionsBauxConf';
import { FACTURE_ETAT, FACTURE_SOUS_TYPE } from '@/configuration/facture/factureConf';
import formatDateIso from '@/services/general/formatIsoDate';
import filtreFactureAchat from '@/components/baux/factureAchat/filtreFactureAchat';
import { get as getCommande } from '@/api/commande';
import { getAll } from '@/api/facture';
import messageErreur from '@/services/general/messageErreur';

const FactureAchat = createNamespacedHelpers('factureAchat');
const Societe = createNamespacedHelpers('societe');
const Permissions = createNamespacedHelpers('permissions');

export default {
  components: {
    breadcrumb,
    TableItemsInView,
    filtreFactureAchat,
  },
  data() {
    return {
      unsubscribeValue: null,
      constantes: {
        FACTURE_ACHAT,
      },
      showFiltre: false,
      commande: null,
      isMounted: false,
    };
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...FactureAchat.mapGetters({
      criteresRecherche: 'criteresRecherche',
      facturesAchat: 'items',
      totalCount: 'total',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      agencesSelectionnees: 'agenceActive',
      lockMenu: 'lockMenu',
    }),
    codeBail() {
      return this.commande?.codeDossier;
    },
    breadcrumb() {
      return [
        { title: this.$t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('listeFactureAchat.breadcrumb.liste'), route: '', isActive: true },
      ];
    },
    nombreCritere() {
      if (!this.isMounted) return 0;
      const banCritereKey = ['auto', 'dates'];
      let nbCritere = calculNombreCritere(this.criteresRecherche, banCritereKey);
      if (this.criteresRecherche.dates.dateDebut) {
        nbCritere += this.criteresRecherche.dates.dateDebut ? 1 : 0;
      }
      if (this.criteresRecherche.dates.dateFin) {
        nbCritere += this.criteresRecherche.dates.dateFin ? 1 : 0;
      }
      return nbCritere > 9 ? '+9' : nbCritere;
    },
    columns() {
      return ['code', 'dateDevisCommande', 'sousType', 'listeIntervenants', 'etat', 'montantHt', 'montantTtc', 'montantRegle', 'edit'];
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          code: this.$i18n.t('listeFactureAchat.tab.code'),
          dateDevisCommande: this.$i18n.t('listeFactureAchat.tab.date'),
          listeIntervenants: this.$i18n.t('listeFactureAchat.tab.fournisseur'),
          etat: this.$i18n.t('listeFactureAchat.tab.etat'),
          montantHt: this.$i18n.t('listeFactureAchat.tab.montantHt'),
          montantTtc: this.$i18n.t('listeFactureAchat.tab.montantTtc'),
          montantRegle: this.$i18n.t('listeFactureAchat.tab.montantPaye'),
          sousType: this.$i18n.t('listeFactureAchat.tab.sousType'),
          edit: '',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: TEXTS,
        requestFunction: ((data) => {
          data.query = cloneDeep(this.criteresRecherche);
          data.query.societeId = this.societeActiveId;
          data.query.sens = SENS_ACHAT;
          if (data.query.tiersId.length > 0) {
            data.query.tiersId = this.criteresRecherche.tiersId.map((tiers) => {
              return tiers.id;
            });
          } else data.query.tiersId = [];
          if (this.criteresRecherche.agencesId.length === 0) {
            data.query.agencesId = this.agencesSelectionnees.map(agence => agence.agenceId);
          }
          if (data.query.dates.dateDebut) {
            data.query.dates.dateDebut = formatDateIso(data.query.dates.dateDebut);
          }
          if (data.query.dates.dateFin) {
            data.query.dates.dateFin = formatDateIso(data.query.dates.dateFin);
          }
          if (data.query.etat.length === 0) data.query.etat = null;
          return this.loadFactureAchat(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.facturesAchat,
            count: this.totalCount,
          };
        }),
        sortable: ['code', 'dateDevisCommande', 'sousType', 'etat', 'montantHt', 'montantTtc'],
        columnsClasses: {
          edit: 'colonneSelected',
        },
      };
    },
    dateEcheance() {
      return this.commande?.echeanciers[0].dateDebut
        && this.commande?.echeanciers[0].dateFin ? {
          dateDebut: new Date(this.commande?.echeanciers[0].dateDebut),
          dateFin: new Date(this.commande?.echeanciers[0].dateFin),
        } : null;
    },
  },
  methods: {
    ...FactureAchat.mapActions({
      loadFactureAchat: 'loadFactureAchat',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    makeRechercheFilter() {
      this.showFiltreAndLockMenu();
      this.recherche();
    },
    getEtat(code) {
      return FACTURE_ETAT.find((etat) => { return etat.code === code; }).libelle;
    },
    getSousType(code) {
      return FACTURE_SOUS_TYPE.find((etat) => { return etat.code === code; }).libelle;
    },
    async showFiltreAndLockMenu() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) await this.$refs.filtreListeFactureAchat.initializeFiltre();
      this.setLockMenu(!this.lockMenu);
    },
    recherche() {
      this.$refs.tableListeFactureAchat.refresh();
    },
    goFirstPage() {
      this.$refs.tableListeFactureAchat.setPage(1);
    },
    finLoading() {
      this.loadingComponent.close();
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    async reinitialiserFiltre() {
      await this.$refs.filtreListeFactureAchat.initializeFiltre();
      this.$refs.filtreListeFactureAchat.viderCritere();
      this.$refs.filtreListeFactureAchat.appliquerCritere();
      this.showFiltre = false;
    },
    async getFacturesIds() {
      const devisCommandeId = this.$route.params.idDevisCommande;
      if (devisCommandeId) {
        const param = {
          query: {
            referenceIds: [devisCommandeId],
            societeId: this.societeActiveId,
            sens: 'achat',
          },
          skipCount: 0,
          MaxResultCount: NO_PAGINATION,
        };
        const factures = (await getAll(param)).data.result.items;

        this.commande = (await getCommande(devisCommandeId)).data.result;
        this.criteresRecherche.idsOrReferences = factures.map(f => f.id);
      }
      if (this.$route.params.idsFactures) {
        this.criteresRecherche.idsOrReferences = [...new Set(
          JSON.parse(this.$route.params.idsFactures))];
      }
    },
    intervenantsNames() {
      return this.commande?.intervenants
        .map(intervenant => intervenant.nomCommercial)[0];
    },
  },
  beforeDestroy() {
    this.unsubscribeValue();
    this.setLockMenu(false);
  },
  async mounted() {
    this.unsubscribeValue = await subscribeMutation(this.$store, 'societe/' + SET_DETECT_CHANGE_SOCIETE, this.recherche, this.goFirstPage);
    this.setInformationPage(this.$t('pageBaux.factureAchat.liste'));
    this.debutLoading();
    try {
      await this.getFacturesIds();
      this.isMounted = true;
      this.finLoading();
    } catch (e) {
      messageErreur(this.$i18n);
      this.finLoading();
      throw e;
    }
  },
};

</script>
